import React, { useEffect, useState, useMemo } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Row,
  Col,
  Space,
  Statistic,
  Divider,
  Card,
  Typography,
  Alert,
  Checkbox,
  Select,
} from "antd";
import {
  DollarOutlined,
  EuroOutlined,
  WalletOutlined,
  ArrowDownOutlined,
  InfoCircleOutlined,
  ReadOutlined,
  TeamOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const PaymentModal = ({ visible, onCancel, salary, onSubmit }) => {
  const { t } = useTranslation();
  const [form] = Form?.useForm();

  const [isPaymentInitiated, setIsPaymentInitiated] = useState(false);
  const [selectedCourseTypes, setSelectedCourseTypes] = useState([]);
  const [calculatedTotals, setCalculatedTotals] = useState({ dzd: 0, eur: 0, usd: 0 });
  // manualAmounts: user edits — null means "not manually edited"
  const [manualAmounts, setManualAmounts] = useState({ dzd: null, eur: null, usd: null });
  const [overpaidCurrencies, setOverpaidCurrencies] = useState([]); // e.g. ['dzd']

  const availableCourseTypes = useMemo(() => {
    if (!salary) return {};
    const hasTutorials = Array?.isArray(salary?.tutorials) && salary?.tutorials?.length > 0;
    const hasSupportCourses = Array?.isArray(salary?.supportCourses) && salary?.supportCourses?.length > 0;
    const hasGroups = Array?.isArray(salary?.groups) && salary?.groups?.length > 0;
    return { hasTutorials, hasSupportCourses, hasGroups };
  }, [salary]);

  const availableTypesCount = Object?.values(availableCourseTypes)?.filter(Boolean)?.length;

  // Build maps of paid amounts per item id (defensive)
  const paidMaps = useMemo(() => {
    const tutorialsPaid = {};
    const supportPaid = {};
    const groupsPaid = {};
    const history = salary?.paymentHistory;
    if (!Array?.isArray(history)) return { tutorialsPaid, supportPaid, groupsPaid };

    history?.forEach((h) => {
      ["tutorials", "supportCourses", "groups"]?.forEach((type) => {
        const idKey =
          type === "tutorials"
            ? "tutorialIds"
            : type === "supportCourses"
            ? "supportCourseIds"
            : "groupIds";
        const ids = (h?.paidFor?.[type]?.[idKey]) || [];
        const amount = h?.paidFor?.[type]?.amount || { dzd: 0, eur: 0, usd: 0 };
        if (!ids || ids?.length === 0) return;

        const perId = {
          dzd: (amount?.dzd || 0) / ids?.length,
          eur: (amount?.eur || 0) / ids?.length,
          usd: (amount?.usd || 0) / ids?.length,
        };

        ids?.forEach((id) => {
          const map = type === "tutorials" ? tutorialsPaid : type === "supportCourses" ? supportPaid : groupsPaid;
          if (!map[id]) map[id] = { dzd: 0, eur: 0, usd: 0 };
          map[id].dzd += perId?.dzd;
          map[id].eur += perId?.eur;
          map[id].usd += perId?.usd;
        });
      });
    });

    return { tutorialsPaid, supportPaid, groupsPaid };
  }, [salary]);

  const isItemFullyPaid = (id, type, itemPayment = {}) => {
    if (!id) return false;
    const map =
      type === "tutorials"
        ? paidMaps?.tutorialsPaid
        : type === "supportCourses"
        ? paidMaps?.supportPaid
        : paidMaps?.groupsPaid;
    const paid = map?.[id] || { dzd: 0, eur: 0, usd: 0 };
    for (const cur of ["dzd", "eur", "usd"]) {
      const needed = itemPayment?.[cur] || 0;
      if (needed > 0) {
        if ((paid[cur] || 0) + 0.0001 < needed) return false;
      }
    }
    return true;
  };

  const allTutorialsPaid = useMemo(() => {
    if (!Array?.isArray(salary?.tutorials) || salary?.tutorials?.length === 0) return false;
    return salary?.tutorials?.every((t) => isItemFullyPaid(t?._id, "tutorials", t?.payment));
  }, [salary?.tutorials, paidMaps]);

  const allSupportPaid = useMemo(() => {
    if (!Array?.isArray(salary?.supportCourses) || salary?.supportCourses?.length === 0) return false;
    return salary?.supportCourses?.every((sc) => isItemFullyPaid(sc?._id, "supportCourses", sc?.payment));
  }, [salary?.supportCourses, paidMaps]);

  const allGroupsPaid = useMemo(() => {
    if (!Array?.isArray(salary?.groups) || salary?.groups?.length === 0) return false;
    return salary?.groups?.every((g) => isItemFullyPaid(g?._id, "groups", g?.payment));
  }, [salary?.groups, paidMaps]);

  // Reset when modal opens
  useEffect(() => {
    if (visible && salary) {
      form?.resetFields();
      setIsPaymentInitiated(false);
      setManualAmounts({ dzd: null, eur: null, usd: null });
      setOverpaidCurrencies([]);
      setCalculatedTotals({ dzd: 0, eur: 0, usd: 0 });

      if (availableTypesCount === 1) {
        const singleType = Object?.keys(availableCourseTypes)?.find((key) => availableCourseTypes[key]);
        const typeMapping = {
          hasTutorials: "tutorials",
          hasSupportCourses: "supportCourses",
          hasGroups: "groups",
        };
        const singleTypeValue = typeMapping[singleType];
        form?.setFieldsValue({ courseTypes: [singleTypeValue] });
        setSelectedCourseTypes([singleTypeValue]);
      } else {
        setSelectedCourseTypes([]);
      }

      form?.setFieldsValue({
        paymentDate: dayjs(),
        comment: "",
        dzdAmount: null,
        eurAmount: null,
        usdAmount: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, salary]);

  // When selections change compute totals and set amounts (if user didn't manually edit)
  const computeSelectedTotals = (values) => {
    const selectedTutorials = (salary?.tutorials || [])?.filter((t) =>
      values?.selectedTutorials?.includes(t?._id)
    );
    const selectedSupportCourses = (salary?.supportCourses || [])?.filter((sc) =>
      values?.selectedSupportCourses?.includes(sc?._id)
    );
    const selectedGroups = (salary?.groups || [])?.filter((g) =>
      values?.selectedGroups?.includes(g?._id)
    );

    let totalDzd = 0,
      totalEur = 0,
      totalUsd = 0;
    [...selectedTutorials, ...selectedSupportCourses, ...selectedGroups]?.forEach((item) => {
      totalDzd += item?.payment?.dzd || 0;
      totalEur += item?.payment?.eur || 0;
      totalUsd += item?.payment?.usd || 0;
    });

    const totals = { dzd: totalDzd, eur: totalEur, usd: totalUsd };
    setCalculatedTotals(totals);

    // If user hasn't manual-edited a currency, set it to calculated value
    const toSet = {};
    if (manualAmounts?.dzd === null) toSet.dzdAmount = totals?.dzd > 0 ? totals?.dzd : null;
    if (manualAmounts?.eur === null) toSet.eurAmount = totals?.eur > 0 ? totals?.eur : null;
    if (manualAmounts?.usd === null) toSet.usdAmount = totals?.usd > 0 ? totals?.usd : null;

    if (Object?.keys(toSet)?.length > 0) form?.setFieldsValue(toSet);

    // Determine isPaymentInitiated (either calculated totals or manual amounts)
    const usedDzd = manualAmounts?.dzd !== null ? manualAmounts?.dzd : totals?.dzd;
    const usedEur = manualAmounts?.eur !== null ? manualAmounts?.eur : totals?.eur;
    const usedUsd = manualAmounts?.usd !== null ? manualAmounts?.usd : totals?.usd;
    setIsPaymentInitiated((usedDzd || usedEur || usedUsd) > 0);

    // update overpaid status
    const over = [];
    if ((manualAmounts?.dzd !== null ? manualAmounts?.dzd : totals?.dzd) > totals?.dzd + 0.0001) over?.push("dzd");
    if ((manualAmounts?.eur !== null ? manualAmounts?.eur : totals?.eur) > totals?.eur + 0.0001) over?.push("eur");
    if ((manualAmounts?.usd !== null ? manualAmounts?.usd : totals?.usd) > totals?.usd + 0.0001) over?.push("usd");
    setOverpaidCurrencies(over);
  };

  // Called whenever any form value changes (selection or amounts)
  const handleSelectionChange = (changedValues, allValues) => {
    // If the user changes amount inputs, update manualAmounts and re-evaluate
    if ("dzdAmount" in changedValues) {
      const val = changedValues?.dzdAmount;
      setManualAmounts((m) => ({ ...m, dzd: val === undefined ? null : val }));
    }
    if ("eurAmount" in changedValues) {
      const val = changedValues?.eurAmount;
      setManualAmounts((m) => ({ ...m, eur: val === undefined ? null : val }));
    }
    if ("usdAmount" in changedValues) {
      const val = changedValues?.usdAmount;
      setManualAmounts((m) => ({ ...m, usd: val === undefined ? null : val }));
    }

    // compute totals based on selected items from allValues (safe defaults)
    const values = {
      selectedTutorials: allValues?.selectedTutorials || [],
      selectedSupportCourses: allValues?.selectedSupportCourses || [],
      selectedGroups: allValues?.selectedGroups || [],
    };
    computeSelectedTotals(values);
  };

  // Remove selected items that became paid (defensive)
  useEffect(() => {
    if (!visible || !salary) return;
    const values = form?.getFieldsValue(["selectedTutorials", "selectedSupportCourses", "selectedGroups"]);
    const newValues = {};

    if (Array?.isArray(values?.selectedTutorials)) {
      const filtered = values?.selectedTutorials?.filter((id) => {
        const item = salary?.tutorials?.find((t) => t?._id === id) || {};
        return !isItemFullyPaid(id, "tutorials", item?.payment);
      });
      if (filtered?.length !== values?.selectedTutorials?.length) newValues.selectedTutorials = filtered;
    }

    if (Array?.isArray(values?.selectedSupportCourses)) {
      const filtered = values?.selectedSupportCourses?.filter((id) => {
        const item = salary?.supportCourses?.find((sc) => sc?._id === id) || {};
        return !isItemFullyPaid(id, "supportCourses", item?.payment);
      });
      if (filtered?.length !== values?.selectedSupportCourses?.length) newValues.selectedSupportCourses = filtered;
    }

    if (Array?.isArray(values?.selectedGroups)) {
      const filtered = values?.selectedGroups?.filter((id) => {
        const item = salary?.groups?.find((g) => g?._id === id) || {};
        return !isItemFullyPaid(id, "groups", item?.payment);
      });
      if (filtered?.length !== values?.selectedGroups?.length) newValues.selectedGroups = filtered;
    }

    if (Object?.keys(newValues)?.length > 0) {
      form?.setFieldsValue(newValues);
      // Recompute totals after removal
      const valuesAfter = {
        selectedTutorials: newValues?.selectedTutorials ?? values?.selectedTutorials,
        selectedSupportCourses: newValues?.selectedSupportCourses ?? values?.selectedSupportCourses,
        selectedGroups: newValues?.selectedGroups ?? values?.selectedGroups,
      };
      computeSelectedTotals(valuesAfter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paidMaps, visible, salary]);

  const sumAmount = (items = []) =>
    items?.reduce(
      (acc, item) => ({
        dzd: acc?.dzd + (item?.payment?.dzd || 0),
        eur: acc?.eur + (item?.payment?.eur || 0),
        usd: acc?.usd + (item?.payment?.usd || 0),
      }),
      { dzd: 0, eur: 0, usd: 0 }
    );

  // Submit -> show confirmation modal; allow overpaid submission
  const handleSubmit = async () => {
    try {
      const values = await form?.validateFields();
      // compute final used amounts (manual if present else calculated)
      const usedDzd = manualAmounts?.dzd !== null ? manualAmounts?.dzd : calculatedTotals?.dzd;
      const usedEur = manualAmounts?.eur !== null ? manualAmounts?.eur : calculatedTotals?.eur;
      const usedUsd = manualAmounts?.usd !== null ? manualAmounts?.usd : calculatedTotals?.usd;

      if (!isPaymentInitiated && !usedDzd && !usedEur && !usedUsd) {
        // nothing to pay
        return;
      }

      // Build selected items
      const selectedTutorials = (salary?.tutorials || [])?.filter((t) =>
        (form?.getFieldValue("selectedTutorials") || [])?.includes(t?._id)
      );
      const selectedSupportCourses = (salary?.supportCourses || [])?.filter((sc) =>
        (form?.getFieldValue("selectedSupportCourses") || [])?.includes(sc?._id)
      );
      const selectedGroups = (salary?.groups || [])?.filter((g) =>
        (form?.getFieldValue("selectedGroups") || [])?.includes(g?._id)
      );

      const paymentData = {
        instructorId: salary?.instructorId?._id,
        paymentMade: {
          dzd: usedDzd || 0,
          eur: usedEur || 0,
          usd: usedUsd || 0,
        },
        comment: values?.comment,
        paymentDate: values?.paymentDate,
        paidFor: {
          tutorials: { tutorialIds: selectedTutorials?.map((t) => t?._id), amount: sumAmount(selectedTutorials) },
          supportCourses: { supportCourseIds: selectedSupportCourses?.map((sc) => sc?._id), amount: sumAmount(selectedSupportCourses) },
          groups: { groupIds: selectedGroups?.map((g) => g?._id), amount: sumAmount(selectedGroups) },
        },
      };

      // Show confirmation modal
      Modal?.confirm({
        title: t("salaries.payment.modal.confirmTitle") || "Confirm payment",
        content: (
          <div>
            <p>{t("salaries.payment.modal.confirmMessage") || "You're about to record this payment:"}</p>
            <ul>
              <li>DZD: {paymentData?.paymentMade?.dzd}</li>
              <li>EUR: {paymentData?.paymentMade?.eur}</li>
              <li>USD: {paymentData?.paymentMade?.usd}</li>
              <li>{t("salaries.payment.modal.comment") || "Comment"}: {paymentData?.comment}</li>
            </ul>
            <p>
              {t("salaries.payment.modal.confirmNote") ||
                "You can proceed even if some amounts exceed the selected items total."}
            </p>
          </div>
        ),
        onOk: async () => {
          await onSubmit(paymentData);
        },
        okText: t("salaries.payment.modal.confirmOk") || "Confirm",
        cancelText: t("common.cancel") || "Cancel",
      });
    } catch (err) {
      console?.error("Validation error:", err);
    }
  };

  const getCurrencyIcon = (currency) => {
    switch ((currency || "")?.toLowerCase()) {
      case "eur":
        return <EuroOutlined />;
      case "usd":
        return <DollarOutlined />;
      default:
        return <WalletOutlined />;
    }
  };

  const courseTypeOptions = [
    {
      label: (
        <>
          <VideoCameraOutlined /> {t("salaries.payment.modal.tutorials")}
        </>
      ),
      value: "tutorials",
      disabled: !availableCourseTypes?.hasTutorials || allTutorialsPaid,
    },
    {
      label: (
        <>
          <ReadOutlined /> {t("salaries.payment.modal.supportCourses")}
        </>
      ),
      value: "supportCourses",
      disabled: !availableCourseTypes?.hasSupportCourses || allSupportPaid,
    },
    {
      label: (
        <>
          <TeamOutlined /> {t("salaries.payment.modal.groups")}
        </>
      ),
      value: "groups",
      disabled: !availableCourseTypes?.hasGroups || allGroupsPaid,
    },
  ];

  // Prepare top-level overpaid message if any
  const overpaidMessage = overpaidCurrencies?.length
    ? `${t("salaries.payment.modal.overpaid") || "Entered amount exceeds selected total for:"} ${overpaidCurrencies?.join(
        ", "
      )?.toUpperCase()}`
    : null;

  return (
    <Modal
      title={t("salaries.payment.modal.title")}
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      okText={t("salaries.payment.modal.submit")}
      cancelText={t("common.cancel")}
      width={800}
      okButtonProps={{ disabled: !isPaymentInitiated && !(manualAmounts?.dzd || manualAmounts?.eur || manualAmounts?.usd) }}
      bodyStyle={{ paddingBottom: 0 }}
    >
      <div style={{ marginBottom: 12 }}>
        <Text strong>{t("salaries.payment.modal.instructor")}:</Text>{" "}
        <Text type="secondary" style={{ fontSize: 16 }}>
          {salary?.instructorId?.fullName}
        </Text>
      </div>
      {overpaidMessage ? (
        <Alert
          message={overpaidMessage}
          description={t("salaries.payment.modal.overpaidDescription") || "You may still proceed if desired."}
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
        />
      ) : null}
      <Form form={form} layout="vertical" onValuesChange={handleSelectionChange}>
        <Card size="small" title={t("salaries.payment.modal.selectionTitle")} style={{ marginBottom: 16 }}>
          {availableTypesCount > 1 && (
            <Form.Item name="courseTypes" label={t("salaries.payment.modal.courseType")}>
              <Checkbox.Group options={courseTypeOptions} onChange={(values) => setSelectedCourseTypes(values)} />
            </Form.Item>
          )}

          {selectedCourseTypes?.includes("tutorials") && (
            <Form.Item name="selectedTutorials" label={t("salaries.payment.modal.tutorials")}>
              <Select mode="multiple" placeholder={t("salaries.payment.modal.selectTutorials")}>
                {(salary?.tutorials || [])?.map((tut) => {
                  const paid = isItemFullyPaid(tut?._id, "tutorials", tut?.payment);
                  return (
                    <Option key={tut?._id} value={tut?._id} disabled={paid}>
                      {tut?.tutorialId?.title || t("common.unnamed")}({tut?.payment?.dzd || 0}DZD)
                                            {paid ? <Text type="success"> &nbsp;(Paid)</Text> : null}
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          )}

          {selectedCourseTypes?.includes("supportCourses") && (
            <Form.Item name="selectedSupportCourses" label={t("salaries.payment.modal.supportCourses")}>
              <Select mode="multiple" placeholder={t("salaries.payment.modal.selectSupportCourses")}>
                {(salary?.supportCourses || [])?.map((sc) => {
                  const paid = isItemFullyPaid(sc?._id, "supportCourses", sc?.payment);
                  return (
                    <Option key={sc?._id} value={sc?._id} disabled={paid}>
                      {sc?.supportCourseId?.description?.substring(0, 50) || t("common.unnamed")}... ({sc?.payment?.dzd || 0}DZD)
                                            {paid ? <Text type="success"> &nbsp;(Paid)</Text> : null}
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          )}

          {selectedCourseTypes?.includes("groups") && (
            <Form.Item name="selectedGroups" label={t("salaries.payment.modal.groups")}>
              <Select mode="multiple" placeholder={t("salaries.payment.modal.selectGroups")}>
                {(salary?.groups || [])?.map((g) => {
                  const paid = isItemFullyPaid(g?._id, "groups", g?.payment);
                  return (
                    <Option key={g?._id} value={g?._id} disabled={paid}>
                      {g?.groupId?.groupName || t("common.unnamed")}({g?.payment?.dzd || 0}DZD)
                                            {paid ? <Text type="success"> &nbsp;(Paid)</Text> : null}
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          )}
        </Card>

        <Alert
          message={t("salaries.payment.modal.info")}
          description={t("salaries.payment.modal.info.description_select")}
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 16 }}
        />

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="dzdAmount"
              label={`${t("salaries.payment.modal.amount")} (DZD)`}
              validateStatus={overpaidCurrencies?.includes("dzd") ? "error" : ""}
              help={overpaidCurrencies?.includes("dzd") ? t("salaries.payment.modal.overpaidFieldHelp") || "Entered amount is greater than selected DZD total." : null}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="0"
                min={0}
                formatter={(v) => (v === null || v === undefined ? "" : v)}
                parser={(v) => (v === "" ? null : Number(v))}
                value={form?.getFieldValue("dzdAmount")}
                onChange={(val) => {
                  form?.setFieldsValue({ dzdAmount: val });
                  setManualAmounts((m) => ({ ...m, dzd: val === undefined ? null : val }));
                  // recompute overpaid using current selected totals
                  const used = val === null || val === undefined ? calculatedTotals?.dzd : Number(val);
                  const over = [...overpaidCurrencies]?.filter((c) => c !== "dzd");
                  if (used > calculatedTotals?.dzd + 0.0001) {
                    if (!over?.includes("dzd")) over?.push("dzd");
                  } else {
                    const i = over?.indexOf("dzd");
                    if (i !== -1) over?.splice(i, 1);
                  }
                  setOverpaidCurrencies(over);
                  // update initiation
                  const usedD = used;
                  const usedE = manualAmounts?.eur !== null ? manualAmounts?.eur : calculatedTotals?.eur;
                  const usedU = manualAmounts?.usd !== null ? manualAmounts?.usd : calculatedTotals?.usd;
                  setIsPaymentInitiated((usedD || usedE || usedU) > 0);
                }}
                // show currency icon inline by prefix: but InputNumber doesn't accept prefix easily; keep simple
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="eurAmount"
              label={`${t("salaries.payment.modal.amount")} (EUR)`}
              validateStatus={overpaidCurrencies?.includes("eur") ? "error" : ""}
              help={overpaidCurrencies?.includes("eur") ? t("salaries.payment.modal.overpaidFieldHelp") || "Entered amount is greater than selected EUR total." : null}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="0"
                min={0}
                formatter={(v) => (v === null || v === undefined ? "" : v)}
                parser={(v) => (v === "" ? null : Number(v))}
                value={form?.getFieldValue("eurAmount")}
                onChange={(val) => {
                  form?.setFieldsValue({ eurAmount: val });
                  setManualAmounts((m) => ({ ...m, eur: val === undefined ? null : val }));
                  const used = val === null || val === undefined ? calculatedTotals?.eur : Number(val);
                  const over = [...overpaidCurrencies]?.filter((c) => c !== "eur");
                  if (used > calculatedTotals?.eur + 0.0001) {
                    if (!over?.includes("eur")) over?.push("eur");
                  } else {
                    const i = over?.indexOf("eur");
                    if (i !== -1) over?.splice(i, 1);
                  }
                  setOverpaidCurrencies(over);
                  const usedD = manualAmounts?.dzd !== null ? manualAmounts?.dzd : calculatedTotals?.dzd;
                  const usedE = used;
                  const usedU = manualAmounts?.usd !== null ? manualAmounts?.usd : calculatedTotals?.usd;
                  setIsPaymentInitiated((usedD || usedE || usedU) > 0);
                }}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="usdAmount"
              label={`${t("salaries.payment.modal.amount")} (USD)`}
              validateStatus={overpaidCurrencies?.includes("usd") ? "error" : ""}
              help={overpaidCurrencies?.includes("usd") ? t("salaries.payment.modal.overpaidFieldHelp") || "Entered amount is greater than selected USD total." : null}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="0"
                min={0}
                formatter={(v) => (v === null || v === undefined ? "" : v)}
                parser={(v) => (v === "" ? null : Number(v))}
                value={form?.getFieldValue("usdAmount")}
                onChange={(val) => {
                  form?.setFieldsValue({ usdAmount: val });
                  setManualAmounts((m) => ({ ...m, usd: val === undefined ? null : val }));
                  const used = val === null || val === undefined ? calculatedTotals?.usd : Number(val);
                  const over = [...overpaidCurrencies]?.filter((c) => c !== "usd");
                  if (used > calculatedTotals?.usd + 0.0001) {
                    if (!over?.includes("usd")) over?.push("usd");
                  } else {
                    const i = over?.indexOf("usd");
                    if (i !== -1) over?.splice(i, 1);
                  }
                  setOverpaidCurrencies(over);
                  const usedD = manualAmounts?.dzd !== null ? manualAmounts?.dzd : calculatedTotals?.dzd;
                  const usedE = manualAmounts?.eur !== null ? manualAmounts?.eur : calculatedTotals?.eur;
                  const usedU = used;
                  setIsPaymentInitiated((usedD || usedE || usedU) > 0);
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="paymentDate"
          label={t("salaries.payment.modal.date")}
          rules={[{ required: true, message: t("salaries.payment.modal.date.required") }]}
        >
          <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
        </Form.Item>

        <Form.Item
          name="comment"
          label={t("salaries.payment.modal.comment")}
          rules={[{ required: true, message: t("salaries.payment.modal.comment.required") }]}
        >
          <TextArea rows={3} placeholder={t("salaries.payment.modal.comment.placeholder")} />
        </Form.Item>
      </Form>
      <Divider style={{ margin: "12px 0" }} />
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card title={<Space><WalletOutlined />{t("salaries.payment.modal.total")}</Space>} size="small" bordered={false} style={{ backgroundColor: "#f5f5f5" }}>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Statistic title="DZD" value={salary?.totalPayment?.dzd || 0} precision={0} valueStyle={{ color: "#1890ff" }} prefix={getCurrencyIcon("dzd")} />
              </Col>
              <Col span={24}>
                <Statistic title="EUR" value={salary?.totalPayment?.eur || 0} precision={2} valueStyle={{ color: "#52c41a" }} prefix={getCurrencyIcon("eur")} />
              </Col>
              <Col span={24}>
                <Statistic title="USD" value={salary?.totalPayment?.usd || 0} precision={2} valueStyle={{ color: "#722ed1" }} prefix={getCurrencyIcon("usd")} />
              </Col>
            </Row>
          </Card>
        </Col>

        <Col span={12}>
          <Card title={<Space><ArrowDownOutlined />{t("salaries.payment.modal.remaining")}</Space>} size="small" bordered={false} style={{ backgroundColor: "#fffbe6" }}>
            <Row gutter={[16, 16]}>
              {salary?.remainingPayment?.dzd > 0 && (
                <Col span={24}>
                  <Statistic title="DZD" value={salary?.remainingPayment?.dzd} precision={0} valueStyle={{ color: "#1890ff" }} prefix={getCurrencyIcon("dzd")} />
                </Col>
              )}
              {salary?.remainingPayment?.eur > 0 && (
                <Col span={24}>
                  <Statistic title="EUR" value={salary?.remainingPayment?.eur} precision={2} valueStyle={{ color: "#52c41a" }} prefix={getCurrencyIcon("eur")} />
                </Col>
              )}
              {salary?.remainingPayment?.usd > 0 && (
                <Col span={24}>
                  <Statistic title="USD" value={salary?.remainingPayment?.usd} precision={2} valueStyle={{ color: "#722ed1" }} prefix={getCurrencyIcon("usd")} />
                </Col>
              )}
              {!(salary?.remainingPayment?.dzd > 0 || salary?.remainingPayment?.eur > 0 || salary?.remainingPayment?.usd > 0) && (
                <Text type="success" style={{ fontSize: 16, padding: "8px 0", display: "block" }}>
                  {t("salaries.payment.fully")}
                </Text>
              )}
            </Row>
          </Card>
        </Col>
      </Row>
    </Modal>
  );
};

export default PaymentModal;
